import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, wsUrl, API, BACKEND_URL, getToken } from "@/lib/api";
import {
  Radio, Cpu, HardDrive, Wifi, Volume2, VolumeX,
  Maximize2, X, Settings2, SkipForward, Wand2, Plus, Power,
} from "lucide-react";
import ShakaVideoTile from "@/components/ShakaVideoTile";
import AudioMeter from "@/components/AudioMeter";
import { getOrCreateGraph, resumeContext, setChannelGain } from "@/lib/audioGraph";
import { toast } from "sonner";
import ChannelSettingsDialog from "./dashboard/ChannelSettingsDialog";
import NewChannelDialog from "./dashboard/NewChannelDialog";
import DvrScrubBar from "./dashboard/DvrScrubBar";
import PublishTodayPanel from "./dashboard/PublishTodayPanel";
import { StatChip, AudioControl } from "./dashboard/DashboardChrome";
import { audioProfileOf } from "./dashboard/audioProfile";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

/** Demo ad HLS used to visualise a SCTE-35 → ad substitution on the fly. */
const DEMO_AD_HLS =
  "https://test-streams.mux.dev/dai-discontinuity-deltatre/manifest.m3u8";




/** DVR scrub bar — reads the seekable window from the underlying <video> element. */

const LOGO_CORNER = {
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2",
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "bottom-2 right-2",
};

// Channel logo / on-air bug overlaid on the preview + fullscreen tiles.
// Falls back to the tenant brand logo for channels that haven't set their own.
function ChannelLogoBug({ channel, tenant }) {
  const token = getToken() || "";
  const now = new Date();
  // Overnight-logo daypart must switch at the channel's LOCAL hour, not the
  // viewer's UTC hour. Format the current time in the channel's timezone
  // (falling back to the tenant tz, then UTC).
  const _tz = channel?.timezone || tenant?.timezone || "UTC";
  let hm;
  try {
    hm = new Intl.DateTimeFormat("en-GB", {
      timeZone: _tz, hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(now);
  } catch {
    hm = `${String(now.getUTCHours()).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(2, "0")}`;
  }
  const inWin = (n, s, e) => {
    const m = (x) => { const [a, b] = (x || "0:0").split(":"); return (+a) * 60 + (+b); };
    const N = m(n), S = m(s), E = m(e);
    return S <= E ? (S <= N && N < E) : (N >= S || N < E);
  };
  let url = null;
  let opacity = channel?.logo_opacity ?? 0.9;
  let isFallback = false;
  if (channel?.logo_dayparts_enabled && channel?.logo_overnight_url
      && inWin(hm, channel.overnight_start || "00:00", channel.overnight_end || "06:00")) {
    url = `${BACKEND_URL}${channel.logo_overnight_url}&auth=${encodeURIComponent(token)}`;  // logo_url already starts with /api
    opacity = channel?.logo_overnight_opacity ?? 0.5;
  } else if (channel?.logo_url) {
    url = `${BACKEND_URL}${channel.logo_url}?auth=${encodeURIComponent(token)}`;
  } else if (tenant?.branding?.logo_url) {
    // Tenant brand-logo fallback — a full external URL, rendered directly.
    url = tenant.branding.logo_url;
    isFallback = true;
  }
  if (!url) return null;
  const pos = LOGO_CORNER[channel.logo_position] || LOGO_CORNER["top-right"];
  return (
    <img
      src={url}
      alt=""
      className={`absolute ${pos} z-[15] pointer-events-none object-contain drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]`}
      style={{ width: `${channel.logo_size_pct || 14}%`, opacity }}
      data-testid={`channel-logo-bug-${channel.slug}${isFallback ? "-tenant-fallback" : ""}`}
    />
  );
}


// Shared with the backend (_flash_windows / _clean_title) so tile + burn-in match.
const OVERLAY_NOISE = new Set(["official","music","video","lyric","lyrics","audio","hd","4k","clip","officiel","visualizer","mv","hq","full"]);
function flashWindows(dur, count, d) {
  count = Math.max(1, Math.min(parseInt(count || 2, 10), 5));
  d = Math.max(3, Math.min(Number(d || 10), 60));
  const first = 10, last = dur - 10 - d;
  if (count === 1 || last <= first) {
    if (dur >= first + d) return [[first, first + d]];
    if (dur > d + 1) return [[Math.max(0, dur - d - 1), dur - 1]];
    return [];
  }
  const step = (last - first) / (count - 1);
  return Array.from({ length: count }, (_, i) => [first + i * step, first + i * step + d]);
}
function cleanTitle(name, artist) {
  let base = (name || "").replace(/\.[A-Za-z0-9]{2,4}$/, "").trim();
  base = base.replace(/[([{][^)\]}]*[)\]}]/g, " ");
  if (artist) {
    const pre = artist.toLowerCase() + " - ";
    if (base.toLowerCase().startsWith(pre)) base = base.slice(pre.length);
    else if (base.includes(" - ")) base = base.split(" - ").slice(1).join(" - ");
  }
  base = base.replace(/\s+/g, " ").replace(/^[\s\-_|]+|[\s\-_|]+$/g, "");
  let toks = base.split(" ");
  while (toks.length && OVERLAY_NOISE.has(toks[toks.length - 1].toLowerCase().replace(/[^a-z0-9]/g, ""))) toks.pop();
  return toks.join(" ").replace(/^[\s\-_|]+|[\s\-_|]+$/g, "") || base;
}

// Broadcast graphics overlays that live INSIDE the video container so they
// scale with fullscreen. Category badge (top-left, whole clip) + song-info
// lower-third (bottom-left) that flashes per the channel's flash config.
// Never shown on demo/fallback.
function BroadcastOverlays({ cdn, active, channelColor, videoEl }) {
  const [songVisible, setSongVisible] = React.useState(false);
  const assetId = cdn?.asset_id;
  const dur = (cdn && cdn.trim_in_sec != null && cdn.trim_out_sec != null)
    ? Math.max(0, cdn.trim_out_sec - cdn.trim_in_sec)
    : (cdn?.asset_duration_sec || 0);

  React.useEffect(() => {
    if (!active || !assetId || !videoEl || !dur) { setSongVisible(false); return; }
    const windows = flashWindows(dur, cdn?.song_flash_count, cdn?.song_flash_duration_sec);
    const inFlash = (t) => windows.some(([s, e]) => t >= s && t <= e);
    const onTime = () => {
      let t = videoEl.currentTime || 0;
      if (cdn.trim_in_sec != null && cdn.trim_out_sec != null) t = Math.max(0, t - cdn.trim_in_sec);
      setSongVisible(inFlash(t));
    };
    videoEl.addEventListener("timeupdate", onTime);
    onTime();
    return () => videoEl.removeEventListener("timeupdate", onTime);
  }, [assetId, active, videoEl, dur, cdn?.trim_in_sec, cdn?.trim_out_sec, cdn?.song_flash_count, cdn?.song_flash_duration_sec]);

  if (!active || !cdn) return null;
  const primary = cdn.brand_primary_color || channelColor || "#111111";
  const textColor = cdn.brand_text_color || "#FFFFFF";
  const songBits = [cdn.artist_name, cleanTitle(cdn.asset_name, cdn.artist_name), cdn.release_year ? String(cdn.release_year) : null].filter(Boolean);
  const songInfo = songBits.join(" — ");
  const category = cdn.category_name;

  return (
    <>
      {category && (
        <div
          className="absolute top-3 left-3 z-20 pointer-events-none rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wide shadow-lg"
          style={{ background: primary, color: textColor }}
          data-testid="overlay-category-badge"
        >
          {category}
        </div>
      )}
      {songInfo && (
        <div
          className="absolute bottom-4 left-3 z-20 pointer-events-none rounded-md px-3 py-1.5 text-sm font-semibold shadow-lg"
          style={
            cdn.song_flash_style === "slide"
              ? {
                  background: primary, color: textColor, maxWidth: "80%",
                  opacity: songVisible ? 0.85 : 0,
                  transform: songVisible ? "translateX(0)" : "translateX(-130%)",
                  transition: "transform 400ms ease-out, opacity 400ms ease-out",
                }
              : {
                  background: primary, color: textColor, opacity: songVisible ? 0.85 : 0,
                  transition: "opacity 400ms ease-in-out", maxWidth: "80%",
                }
          }
          data-testid="overlay-song-banner"
        >
          {songInfo}
        </div>
      )}
    </>
  );
}


// Renders a channel's configured 14-layer Brand Identity / Overlay Graphics as
// scaled CSS overlays inside the video container. Coordinates are authored on a
// 1920x1080 canvas and converted to %; font-size uses container-query units so
// text scales with the tile (and fullscreen). Mirrors the ffmpeg burn-in.
function gfxLayerActiveNow(props, d) {
  const sc = (props && props.sched) || {};
  if (!sc.on) return true;
  const jsDow = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  const hm = (s) => { const [h, m] = String(s).split(":"); return (+h) * 60 + (+m); };
  const winOn = (w) => {
    const days = w.days || [];
    if (days.length && !days.includes(jsDow)) return false;
    const s = hm(w.s), e = hm(w.e);
    if (isNaN(s) || isNaN(e)) return false;
    if (s === e) return true;
    if (s < e) return minutes >= s && minutes < e;
    return minutes >= s || minutes < e;
  };
  if ((sc.windows || []).some(winOn)) return true;
  const oo = sc.oneoff;
  if (oo && oo.s && oo.e) {
    const now = d.getTime();
    const s = new Date(oo.s).getTime(), e = new Date(oo.e).getTime();
    if (!isNaN(s) && !isNaN(e) && now >= s && now < e) return true;
  }
  return false;
}

function ConfiguredGraphicsLayers({ channelId, active }) {
  const [layers, setLayers] = React.useState([]);
  const [nowStr, setNowStr] = React.useState("");
  const [feeds, setFeeds] = React.useState({});
  React.useEffect(() => {
    let alive = true;
    if (!channelId) return;
    api.get(`/graphics?channel_id=${channelId}`)
      .then((r) => { if (alive) setLayers(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setLayers([]); });
    return () => { alive = false; };
  }, [channelId]);
  React.useEffect(() => {
    const tick = () => setNowStr(new Date().toLocaleTimeString([], { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  // Live text feeds + weather for dynamic layers — refresh every 5 min.
  React.useEffect(() => {
    let alive = true;
    const feedLayers = layers.filter((l) => l.visible && (l.props || {}).feed_url);
    const wxLayers = layers.filter((l) => l.visible && l.type === "weather" && (l.props || {}).location);
    if (!feedLayers.length && !wxLayers.length) { setFeeds({}); return; }
    const pull = () => {
      feedLayers.forEach((l) => api.get(`/graphics/${l.id}/feed`)
        .then((r) => { if (alive && r.data?.text != null) setFeeds((f) => ({ ...f, [l.id]: r.data.text })); }).catch(() => {}));
      wxLayers.forEach((l) => api.get(`/graphics/${l.id}/weather`)
        .then((r) => { if (alive && r.data?.text != null) setFeeds((f) => ({ ...f, [l.id]: r.data.text })); }).catch(() => {}));
    };
    pull();
    const id = setInterval(pull, 300000);
    return () => { alive = false; clearInterval(id); };
  }, [layers]);
  if (!active) return null;
  const nowDate = new Date(); // re-evaluated each render (1s clock tick)
  const vis = layers
    .filter((l) => l.visible)
    .sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
  if (!vis.length) return null;
  const CW = 1920, CH = 1080;
  return (
    <div style={{ position: "absolute", inset: 0, containerType: "size", pointerEvents: "none", zIndex: 16 }} data-testid="configured-gfx-root">
      <style>{`@keyframes gfx-ticker{0%{transform:translateX(100%)}100%{transform:translateX(-100%)}}`}</style>
      {vis.map((l) => {
        const p = l.props || {};
        const on = gfxLayerActiveNow(p, nowDate);
        const anim = p.anim || "none";
        const layerOp = l.opacity != null ? l.opacity : 1;
        const offTransform = anim === "slide" ? "translateX(-8%)" : "none";
        const box = {
          position: "absolute",
          left: `${(l.x / CW) * 100}%`,
          top: `${(l.y / CH) * 100}%`,
          width: `${(l.width / CW) * 100}%`,
          height: `${(l.height / CH) * 100}%`,
          opacity: on ? layerOp : 0,
          transform: on ? "translateX(0)" : offTransform,
          transition: anim === "slide" ? "opacity 400ms ease, transform 400ms ease"
            : anim === "fade" ? "opacity 400ms ease" : "none",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        };
        const isImg = (l.type === "image" || l.type === "logo" || l.type === "apng") && p.image_url;
        if (isImg) {
          return <img key={l.id} src={p.image_url} alt={l.name} data-testid={`gfx-layer-${l.layer_index}`}
            style={{ ...box, objectFit: p.fit || "contain" }} />;
        }
        const txt = l.type === "clock" ? nowStr : (feeds[l.id] || p.text || "");
        const fs = `${Math.max(2.4, (l.height / CH) * 100 * 0.5)}cqh`;
        const hasBg = p.background || l.type === "lower_third" || l.type === "ticker";
        const inner = {
          width: "100%",
          color: p.color || p.text_color || "#FFFFFF",
          background: p.background || (hasBg ? "rgba(0,0,0,0.55)" : "transparent"),
          fontSize: fs,
          fontWeight: 700,
          lineHeight: 1.15,
          padding: hasBg ? "0.2em 0.5em" : 0,
          borderRadius: hasBg ? "0.25em" : 0,
          whiteSpace: l.type === "ticker" ? "nowrap" : "normal",
          ...(l.type === "ticker" ? { display: "inline-block", animation: "gfx-ticker 14s linear infinite" } : {}),
        };
        return (
          <div key={l.id} style={box} data-testid={`gfx-layer-${l.layer_index}`}>
            <div style={inner}>{txt}</div>
          </div>
        );
      })}
    </div>
  );
}


// "On-air now" strip under each tile: a mini timeline of the song lower-third
// flash windows across the current clip, highlighting the window the playhead
// is currently in. Mirrors flashWindows() so it matches the tile + burn-in.
function FlashWindowStrip({ cdn, active, videoEl }) {
  const [t, setT] = React.useState(0);
  const assetId = cdn?.asset_id;
  const dur = (cdn && cdn.trim_in_sec != null && cdn.trim_out_sec != null)
    ? Math.max(0, cdn.trim_out_sec - cdn.trim_in_sec)
    : (cdn?.asset_duration_sec || 0);

  React.useEffect(() => {
    if (!active || !assetId || !videoEl || !dur) { setT(0); return; }
    const onTime = () => {
      let tt = videoEl.currentTime || 0;
      if (cdn.trim_in_sec != null && cdn.trim_out_sec != null) tt = Math.max(0, tt - cdn.trim_in_sec);
      setT(tt);
    };
    videoEl.addEventListener("timeupdate", onTime);
    onTime();
    return () => videoEl.removeEventListener("timeupdate", onTime);
  }, [assetId, active, videoEl, dur, cdn?.trim_in_sec, cdn?.trim_out_sec]);

  if (!active || !cdn || !dur) return null;
  const songBits = [cdn.artist_name, cleanTitle(cdn.asset_name, cdn.artist_name), cdn.release_year ? String(cdn.release_year) : null].filter(Boolean);
  if (!songBits.length) return null;
  const windows = flashWindows(dur, cdn?.song_flash_count, cdn?.song_flash_duration_sec);
  if (!windows.length) return null;
  const activeIdx = windows.findIndex(([s, e]) => t >= s && t <= e);
  const total = windows.length;
  let label;
  if (activeIdx >= 0) label = `Song info showing · flash ${activeIdx + 1}/${total}`;
  else {
    const next = windows.find(([s]) => s > t);
    label = next ? `Next flash in ${Math.max(0, Math.ceil(next[0] - t))}s` : "Overlay done for this clip";
  }
  const primary = cdn.brand_primary_color || "#e11d48";
  return (
    <div className="mb-2" data-testid="flash-window-strip">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-500">On-air overlay</span>
        <span className={`text-[9px] font-mono ${activeIdx >= 0 ? "text-fuchsia-300" : "text-zinc-400"}`} data-testid="flash-window-label">{label}</span>
      </div>
      <div className="relative h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        {windows.map(([s, e], i) => (
          <div
            key={i}
            className={`absolute top-0 h-full rounded-full transition-opacity ${i === activeIdx ? "opacity-100 animate-pulse" : "opacity-50"}`}
            style={{ left: `${(s / dur) * 100}%`, width: `${Math.max(1, ((e - s) / dur) * 100)}%`, background: primary }}
            data-testid={`flash-window-seg-${i}`}
          />
        ))}
        <div
          className="absolute top-[-2px] h-[10px] w-[2px] bg-emerald-300"
          style={{ left: `${Math.min(100, (t / dur) * 100)}%` }}
          data-testid="flash-window-playhead"
        />
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const canWrite = user && ["super_admin", "tenant_admin", "scheduler"].includes(user.role);
  const [channels, setChannels] = useState([]);
  const [tick, setTick] = useState(null);
  const [soloChannelId, setSoloChannelId] = useState(null);
  const [volumes, setVolumes] = useState({});
  const [fullscreenChannelId, setFullscreenChannelId] = useState(null);
  const [settingsChannel, setSettingsChannel] = useState(null);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const canCreateChannel = user && ["super_admin", "tenant_admin"].includes(user.role);
  // Track which channels are currently in an ad-substitution demo swap
  const [adSwap, setAdSwap] = useState({}); // channel_id -> boolean
  // Per-channel CDN playback state — {hls_url, title, asset_id} when the current
  // schedule item's asset is published to Cloudflare Stream, null otherwise.
  const [cdnPlayback, setCdnPlayback] = useState({});
  // User's per-channel preference to prefer CDN over origin. Defaults to true
  // whenever CDN playback is available (opt-out per tile).
  const [cdnPrefer, setCdnPrefer] = useState({});

  const videoRefs = useRef({});
  const wasDuckingRef = useRef({}); // last ducking state per channel

  const getVolume = (id) => (id in volumes ? volumes[id] : 0.8);
  const setVolume = (id, v) => setVolumes((prev) => ({ ...prev, [id]: v }));

  // ESC exits fullscreen
  useEffect(() => {
    if (!fullscreenChannelId) return;
    const onKey = (e) => e.key === "Escape" && setFullscreenChannelId(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreenChannelId]);

  const setVideoRef = (id) => (el) => {
    if (el) videoRefs.current[id] = el;
    else delete videoRefs.current[id];
  };

  /**
   * Toggle solo — call this from the click handler. It resumes the AudioContext
   * and primes the WebAudio graph SYNCHRONOUSLY inside the user gesture, then
   * schedules the state flip. Doing this in a useEffect (which runs after the
   * gesture chain) means Chrome refuses to resume the context on first-click
   * → silent solo.
   */
  const toggleSolo = (channelId) => {
    // Resume AudioContext inside the gesture (required by Chrome autoplay policy).
    resumeContext();
    // Mute-all path: gain-down every video's graph inside the gesture too.
    const isCurrentlySoloed = soloChannelId === channelId;
    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (!el) return;
      if (id === channelId && !isCurrentlySoloed) {
        // Becoming soloed: unmute native element BEFORE creating the source
        // (Chrome tags muted-at-source clips as silent), create the graph,
        // ramp gain up — all inside the same click gesture so the browser
        // honours createMediaElementSource + resume().
        try { el.muted = false; el.volume = 1; } catch { /* noop */ }
        getOrCreateGraph(el);
        setChannelGain(el, getVolume(id));
      } else {
        // Any tile losing solo (or the one being toggled off): hard-mute
        // native + zero the WebAudio gain.
        try { el.muted = true; } catch { /* noop */ }
        setChannelGain(el, 0);
      }
    });
    setSoloChannelId((prev) => (prev === channelId ? null : channelId));
  };

  // Sync per-channel audio graph gain when solo/volume changes, or when
  // fullscreen toggles (which remounts the <video>) or when the URL flips
  // via SCTE-35 ad substitution (which tears down + reattaches Shaka and
  // can leave the Web Audio graph disconnected until re-primed).
  useEffect(() => {
    resumeContext();
    Object.entries(videoRefs.current).forEach(([id, videoEl]) => {
      if (!videoEl) return;
      const isSolo = id === soloChannelId;
      // Belt-and-suspenders: keep the native muted flag in lockstep so the
      // element never silently reverts to muted after a Shaka reload.
      try { videoEl.muted = !isSolo; } catch { /* noop */ }
      if (isSolo) {
        getOrCreateGraph(videoEl);
        setChannelGain(videoEl, getVolume(id));
      } else {
        setChannelGain(videoEl, 0);
      }
    });
  }, [soloChannelId, volumes, fullscreenChannelId, channels, adSwap]);

  useEffect(() => {
    api.get("/channels").then((r) => setChannels(r.data)).catch(() => { /* noop */ });
    const ws = new WebSocket(wsUrl("/api/ws/channels"));
    ws.onmessage = (e) => {
      try { setTick(JSON.parse(e.data)); } catch { /* noop */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, []);

  const byId = useMemo(
    () => Object.fromEntries((tick?.channels || []).map((c) => [c.id, c])),
    [tick]
  );

  // ---- SCTE-35 duck edge detection → ad substitution demo ----
  useEffect(() => {
    (tick?.channels || []).forEach((live) => {
      // Respect the per-channel SCTE-35 toggle: never swap to ads when off,
      // and clear any lingering swap for a channel that was just disabled.
      if (live.scte35_enabled === false) {
        wasDuckingRef.current[live.id] = false;
        setAdSwap((p) => (p[live.id] ? { ...p, [live.id]: false } : p));
        return;
      }
      const prev = wasDuckingRef.current[live.id];
      const now = !!live.ducking;
      wasDuckingRef.current[live.id] = now;
      if (prev === undefined) return; // first tick, don't swap
      if (!prev && now) {
        // Duck window began → swap to demo ad
        setAdSwap((p) => ({ ...p, [live.id]: true }));
      } else if (prev && !now) {
        // Duck window ended → swap back to programme
        setAdSwap((p) => ({ ...p, [live.id]: false }));
      }
    });
  }, [tick]);

  const streamUrlFor = (c) => {
    if (adSwap[c.id]) return DEMO_AD_HLS;
    // Prefer Cloudflare Stream CDN when the current schedule item is on CDN
    // and the operator hasn't opted out for this tile.
    const cdn = cdnPlayback[c.id];
    if (cdn?.hls_url && cdnPrefer[c.id] !== false) {
      return cdn.hls_url;
    }
    // No current asset on CDN — fall back to the channel-level
    // `demo_preview_hls_url` (a static manifest the tenant controls) rather
    // than the Cloudflare Live Input HLS. The Live Input returns HTTP 204
    // whenever OBS isn't actively pushing, which breaks the tile with a 204
    // and no video. The demo manifest guarantees the tile always plays SOME
    // content so scheduling / graphics can be QA'd without a live source.
    if (cdn?.fallback_hls_url) return cdn.fallback_hls_url;
    return null;
  };

  // Effective trim for whatever streamUrlFor(c) is currently returning — null
  // whenever the tile isn't playing the trimmed CDN asset (ad-swap, operator
  // opted out of CDN, or demo/fallback source), so those never get seeked.
  const trimForCurrent = (c) => {
    if (adSwap[c.id]) return { trimIn: null, trimOut: null };
    const cdn = cdnPlayback[c.id];
    if (cdn?.hls_url && cdnPrefer[c.id] !== false) {
      return { trimIn: cdn.trim_in_sec ?? null, trimOut: cdn.trim_out_sec ?? null };
    }
    return { trimIn: null, trimOut: null };
  };

  // Live "in m:ss" countdown to the next slot switch. Recomputed on each
  // render (the per-second WebSocket `tick` drives re-renders).
  const countdownFor = (switchAt) => {
    if (!switchAt) return null;
    const secs = Math.round((switchAt - Date.now()) / 1000);
    if (secs <= 0 || secs > 6 * 3600) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
  };

  // How far into the current slot we are (0–100), from the end anchor and
  // the slot's total length. Recomputed each render via the WebSocket tick.
  const slotProgress = (switchAt, slotSec) => {
    if (!switchAt || !slotSec) return null;
    const remaining = (switchAt - Date.now()) / 1000;
    const elapsed = slotSec - remaining;
    const pct = (elapsed / slotSec) * 100;
    return Math.min(100, Math.max(0, pct));
  };


  // Poll current-hls per channel every 30s so if the schedule item changes
  // mid-stream we pick up the next CDN URL. Also refreshes when channels
  // list first loads.
  useEffect(() => {
    if (!channels.length) return;
    let cancelled = false;
    const refresh = () => {
      channels.forEach((c) => {
        if (((c.cdn && c.cdn.publish_state) || "idle") !== "live") {
          // Idle channels run no worker — skip current-hls fetch to save requests
          // and clear any stale playback so the tile shows the idle overlay.
          setCdnPlayback((prev) => (prev[c.id] === undefined ? prev : { ...prev, [c.id]: undefined }));
          return;
        }
        api.get(`/cloudflare-stream/channel/${c.id}/current-hls`)
          .then((r) => {
            if (cancelled) return;
            setCdnPlayback((prev) => {
              const next = r.data?.cdn ? {
                hls_url: r.data.hls_url,
                title: r.data.title,
                asset_id: r.data.asset_id,
                scheduleItemId: r.data.schedule_item_id,
                stream_uid: r.data.cloudflare_stream_uid,
                poster_url: r.data.poster_url || null,
                upNext: r.data.next || null,
                upcoming: r.data.upcoming || [],
                currentStart: r.data.current_start_time || null,
                currentEnd: r.data.current_end_time || null,
                currentSlotSec: r.data.current_slot_sec || null,
                nextSwitchAt: r.data.seconds_to_next != null ? Date.now() + r.data.seconds_to_next * 1000 : null,
                trim_in_sec: r.data.trim_in_sec,
                trim_out_sec: r.data.trim_out_sec,
                trim_source: r.data.trim_source,
                asset_name: r.data.asset_name,
                asset_duration_sec: r.data.asset_duration_sec,
                category_name: r.data.category_name,
                artist_name: r.data.artist_name,
                release_year: r.data.release_year,
                brand_primary_color: r.data.brand_primary_color,
                brand_secondary_color: r.data.brand_secondary_color,
                brand_text_color: r.data.brand_text_color,
                song_flash_count: r.data.song_flash_count,
                song_flash_duration_sec: r.data.song_flash_duration_sec,
                song_flash_style: r.data.song_flash_style,
                fallback_hls_url: r.data.fallback_hls_url || null,
              } : {
                hls_url: null,
                fallback_hls_url: r.data?.fallback_hls_url || null,
                reason: r.data?.reason,
              };
              // Only update if changed to avoid re-render churn
              const cur = prev[c.id];
              const sig = (x) => (x?.upcoming || []).map((u) => `${u.start_time}|${u.title}`).join(",");
              if (cur?.hls_url === next?.hls_url
                  && cur?.trim_in_sec === next?.trim_in_sec
                  && cur?.trim_out_sec === next?.trim_out_sec
                  && cur?.poster_url === next?.poster_url
                  && cur?.currentEnd === next?.currentEnd
                  && sig(cur) === sig(next)
                  && cur?.fallback_hls_url === next?.fallback_hls_url) return prev;
              return { ...prev, [c.id]: next };
            });
          })
          .catch(() => { /* endpoint may be unauthorised for operators — noop */ });
      });
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [channels]);

  // Poster prefetch — preload up-next / upcoming thumbnails so the switch
  // is instant with no flash of black.
  const prefetchedRef = useRef(new Set());
  useEffect(() => {
    Object.values(cdnPlayback || {}).forEach((p) => {
      const urls = [p?.poster_url, ...((p?.upcoming || []).map((u) => u.poster_url))];
      urls.forEach((u) => {
        if (u && !prefetchedRef.current.has(u)) {
          prefetchedRef.current.add(u);
          const img = new Image();
          img.src = u;
        }
      });
    });
  }, [cdnPlayback]);


  const updateChannel = (updated) => {
    setChannels((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const pubStateOf = (c) => (c?.cdn?.publish_state) || "idle";
  const [pubBusyId, setPubBusyId] = useState(null);
  const togglePublishState = async (c) => {
    const cur = pubStateOf(c);
    const next = cur === "live" ? "idle" : "live";
    if (cur === "live" && !window.confirm(`Set ${c.name} to Idle? The publish worker will stop until you set it back to Live.`)) return;
    setPubBusyId(c.id);
    try {
      const r = await api.post(`/channels/${c.id}/publish`, { publish_state: next });
      updateChannel(r.data);
      toast.success(`${c.name} → ${next === "live" ? "LIVE — publishing" : "IDLE — worker stopped"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish toggle failed");
    } finally {
      setPubBusyId(null);
    }
  };

  const bulkPublish = async (state) => {
    if (state === "idle" && !window.confirm("Set ALL channels to Idle? Every publish worker will stop until you set them Live again.")) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/channels/publish-bulk", { publish_state: state });
      const fresh = await api.get("/channels");
      setChannels(fresh.data);
      toast.success(`${r.data.changed} channel(s) → ${state === "live" ? "LIVE — publishing" : "IDLE — workers stopped"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk publish failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const fsChannel = fullscreenChannelId ? channels.find((x) => x.id === fullscreenChannelId) : null;

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Channel Dashboard</div>
          <h1 className="font-display font-bold text-2xl mt-1">Live output</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {soloChannelId ? (
              <>
                <span className="dot-live" style={{ background: "#10B981", boxShadow: "0 0 8px #10B981" }} />
                <span className="text-emerald-400" data-testid="audio-solo-indicator">
                  Audio: {channels.find((x) => x.id === soloChannelId)?.name || "—"}
                </span>
                <button
                  onClick={() => setSoloChannelId(null)}
                  data-testid="audio-mute-all"
                  className="ml-2 px-2 py-0.5 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                >
                  Mute all
                </button>
              </>
            ) : (
              <>
                <VolumeX className="w-3.5 h-3.5" />
                <span>Audio muted — hover a tile to solo</span>
              </>
            )}
          </div>
          <div className="text-[10px] font-mono text-zinc-500">
            MASTER: <span className="text-emerald-400">{tick?.master_clock || "—"}</span>
          </div>
          {(() => {
            const idle = channels.filter((c) => pubStateOf(c) !== "live").length;
            if (!channels.length) return null;
            // Rough estimate: one continuous publish worker (ffmpeg copy-mux +
            // overlay burn-in) ≈ ~1 vCPU and ~$22/mo of compute.
            const vcpu = idle * 1;
            const usdMo = idle * 22;
            return (
              <div
                data-testid="idle-savings-badge"
                title={`${idle} of ${channels.length} channels are Idle — their publish workers are stopped, saving roughly ${vcpu} vCPU and ~$${usdMo}/mo of compute. Set a channel to Live to resume it.`}
                className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                  idle > 0
                    ? "text-emerald-300 bg-emerald-950/50 border-emerald-500/40"
                    : "text-zinc-500 bg-black/40 border-zinc-700"
                }`}
              >
                <Cpu className="w-3 h-3" strokeWidth={2} />
                {idle > 0 ? (
                  <span data-testid="idle-savings-text">{idle} idle · ~{vcpu} vCPU · ~${usdMo}/mo saved</span>
                ) : (
                  <span data-testid="idle-savings-text">All live · 0 saved</span>
                )}
              </div>
            );
          })()}
          {canWrite && channels.length > 0 && (() => {
            const liveCount = channels.filter((c) => pubStateOf(c) === "live").length;
            const idleCount = channels.length - liveCount;
            return (
              <div className="flex items-center gap-1.5" data-testid="bulk-publish-controls">
                <button
                  type="button"
                  onClick={() => bulkPublish("live")}
                  disabled={bulkBusy || idleCount === 0}
                  data-testid="bulk-publish-all-btn"
                  title={idleCount === 0 ? "All channels are already live" : `Publish all ${idleCount} idle channel(s)`}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Radio className="w-3.5 h-3.5" strokeWidth={2.5} /> {bulkBusy ? "…" : "Publish all"}
                </button>
                <button
                  type="button"
                  onClick={() => bulkPublish("idle")}
                  disabled={bulkBusy || liveCount === 0}
                  data-testid="bulk-idle-all-btn"
                  title={liveCount === 0 ? "All channels are already idle" : `Stop all ${liveCount} live channel(s)`}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border border-zinc-600 text-zinc-300 hover:bg-zinc-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Power className="w-3.5 h-3.5" strokeWidth={2.5} /> {bulkBusy ? "…" : "Idle all"}
                </button>
              </div>
            );
          })()}
          {canCreateChannel && (
            <button
              type="button"
              onClick={() => setShowNewChannel(true)}
              data-testid="new-channel-btn"
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm bg-fuchsia-600 hover:bg-fuchsia-500 text-white transition-colors"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> New channel
            </button>
          )}
        </div>
      </div>

      {/* Grid — only rendered when NOT in fullscreen (prevents duplicate <video>
          instances that would break the WebAudio graph mapping and silence audio). */}
      {!fullscreenChannelId && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-3">
          {channels.map((c) => {
            const live = byId[c.id] || {};
            const status = live.status || c.status;
            const on = status === "on_air";
            const inAd = !!adSwap[c.id];
            return (
              <div key={c.id} className={`panel overflow-hidden transition-shadow ${soloChannelId === c.id ? "gradient-stroke" : ""}`} style={soloChannelId === c.id ? { boxShadow: "0 0 24px rgba(139,92,246,0.35)" } : undefined} data-testid={`channel-tile-${c.slug}`}>
                <div className="aspect-video relative bg-black group/tile">
                  <ShakaVideoTile
                    ref={setVideoRef(c.id)}
                    url={streamUrlFor(c)}
                    trimIn={trimForCurrent(c).trimIn}
                    trimOut={trimForCurrent(c).trimOut}
                    poster={!inAd && cdnPrefer[c.id] !== false ? cdnPlayback[c.id]?.poster_url : null}
                    brandName={c.name}
                    brandColor={c.color}
                    muted={soloChannelId !== c.id}
                    volume={getVolume(c.id)}
                    testId={`shaka-${c.slug}`}
                  />
                  <ChannelLogoBug channel={c} tenant={tenant} />
                  <BroadcastOverlays
                    cdn={cdnPlayback[c.id]}
                    active={!!(cdnPlayback[c.id]?.hls_url && cdnPrefer[c.id] !== false && !adSwap[c.id])}
                    channelColor={c.color}
                    videoEl={videoRefs.current[c.id]}
                  />
                  <ConfiguredGraphicsLayers
                    channelId={c.id}
                    active={!!(cdnPlayback[c.id]?.hls_url && cdnPrefer[c.id] !== false && !adSwap[c.id])}
                  />

                  <button
                    type="button"
                    data-testid={`fullscreen-${c.slug}`}
                    onClick={() => setFullscreenChannelId(c.id)}
                    aria-label="Expand tile"
                    className="absolute top-2 left-24 z-20 opacity-0 group-hover/tile:opacity-100 p-1 rounded-sm bg-black/70 border border-zinc-700 text-zinc-300 hover:text-white transition-opacity"
                  >
                    <Maximize2 className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    data-testid={`channel-settings-${c.slug}`}
                    onClick={() => setSettingsChannel(c)}
                    aria-label="Channel settings"
                    className="absolute top-2 left-[3.75rem] z-30 opacity-0 group-hover/tile:opacity-100 p-1 rounded-sm bg-black/70 border border-zinc-700 text-zinc-300 hover:text-white transition-opacity"
                  >
                    <Settings2 className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>

                  <div className="absolute top-2 left-2 flex items-center gap-1.5 z-10">
                    {on ? (
                      <>
                        <span className="dot-live" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-red-400 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">Live</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 rounded-full bg-zinc-500 inline-block" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{status}</span>
                      </>
                    )}
                    {inAd && (
                      <span
                        className="ml-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-950/80 border border-amber-500/60 px-1.5 py-0.5 rounded-sm animate-pulse"
                        data-testid={`ad-bug-${c.slug}`}
                        title="SCTE-35 driven ad substitution in progress"
                      >
                        AD
                      </span>
                    )}
                    {(() => {
                      const ap = audioProfileOf(c);
                      const cur = live.audio_lufs;
                      if (cur == null) return null;
                      const delta = cur - ap.loudness_target_lkfs;
                      const outside = Math.abs(delta) > ap.loudness_tolerance_lu;
                      if (!outside) return null;
                      const over = delta > 0;
                      return (
                        <span
                          data-testid={`loudness-alert-${c.slug}`}
                          className="ml-1.5 font-mono text-[10px] uppercase tracking-widest text-red-300 bg-red-950/80 border border-red-500/60 px-1.5 py-0.5 rounded-sm"
                          title={`Live ${cur.toFixed(1)} LKFS is ${Math.abs(delta).toFixed(1)} LU ${over ? "above" : "below"} target ${ap.loudness_target_lkfs} · outside ±${ap.loudness_tolerance_lu} LU`}
                        >
                          {over ? "▲" : "▼"} {Math.abs(delta).toFixed(1)} LU
                        </span>
                      );
                    })()}
                  </div>

                  <AudioControl
                    soloed={soloChannelId === c.id}
                    volume={getVolume(c.id)}
                    onToggleSolo={() => toggleSolo(c.id)}
                    onVolumeChange={(v) => setVolume(c.id, v)}
                    testId={`audio-${c.slug}`}
                  />

                  <div className="absolute top-2 right-14 flex items-center gap-1.5 z-30">
                    <button
                      type="button"
                      data-testid={`publish-badge-${c.slug}`}
                      disabled={!canWrite || pubBusyId === c.id}
                      onClick={() => togglePublishState(c)}
                      title={pubStateOf(c) === "live" ? "Publishing — click to set Idle" : "Idle — click to publish"}
                      className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border transition-colors disabled:opacity-60 ${
                        pubStateOf(c) === "live"
                          ? "text-emerald-200 bg-emerald-950/80 border-emerald-500/60 hover:bg-emerald-900"
                          : "text-zinc-300 bg-black/70 border-zinc-600 hover:text-white"
                      }`}
                    >
                      {pubBusyId === c.id ? "…" : (pubStateOf(c) === "live" ? "● LIVE" : "○ IDLE")}
                    </button>
                    {cdnPlayback[c.id]?.hls_url && !inAd && (
                      <button
                        type="button"
                        onClick={() => setCdnPrefer((p) => ({ ...p, [c.id]: p[c.id] === false }))}
                        data-testid={`cdn-toggle-${c.slug}`}
                        title={cdnPrefer[c.id] !== false
                          ? `Playing via Cloudflare edge · ${cdnPlayback[c.id].title}`
                          : `Origin playback · click to switch to CDN`}
                        className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border transition-colors ${
                          cdnPrefer[c.id] !== false
                            ? "text-emerald-200 bg-emerald-950/80 border-emerald-500/60 hover:bg-emerald-900"
                            : "text-zinc-300 bg-black/70 border-zinc-700 hover:text-white"
                        }`}
                      >
                        {cdnPrefer[c.id] !== false ? "CDN" : "ORIGIN"}
                      </button>
                    )}
                    <div className="font-mono text-[10px] uppercase text-zinc-300 bg-black/60 border border-zinc-700 px-1.5 py-0.5">
                      {c.resolution}
                    </div>
                  </div>
                  <div className="absolute bottom-2 left-2 font-mono text-xs text-emerald-300 bg-black/60 border border-zinc-700 px-2 py-0.5 z-10">
                    {live.current_time || "--:--:--"}
                  </div>
                  <div className="absolute bottom-2 right-14 font-mono text-[10px] text-zinc-300 bg-black/60 border border-zinc-700 px-1.5 py-0.5 z-10">
                    {live.viewers?.toLocaleString?.() || 0} viewers
                  </div>
                  {cdnPlayback[c.id]?.upNext?.title && cdnPrefer[c.id] !== false && !inAd && (
                    <div
                      className="absolute bottom-2 left-1/2 -translate-x-1/2 max-w-[52%] truncate font-mono text-[10px] uppercase tracking-widest text-cyan-200 bg-cyan-950/85 border border-cyan-500/50 px-2 py-0.5 rounded-sm z-10 flex items-center gap-1"
                      data-testid={`up-next-${c.slug}`}
                      title={`Up next: ${cdnPlayback[c.id].upNext.title}`}
                    >
                      <SkipForward className="w-3 h-3 shrink-0" strokeWidth={2} />
                      <span className="truncate">{cdnPlayback[c.id].upNext.title}</span>
                      {(() => {
                        const cd = countdownFor(cdnPlayback[c.id]?.nextSwitchAt);
                        return cd ? <span className="shrink-0 text-cyan-100/90 tabular-nums" data-testid={`up-next-countdown-${c.slug}`}>· in {cd}</span> : null;
                      })()}
                    </div>
                  )}

                  {cdnPlayback[c.id] && !cdnPlayback[c.id].hls_url && cdnPrefer[c.id] !== false && !inAd && canWrite && (
                    <button
                      onClick={() => navigate(`/console/scheduler?channel=${c.id}&action=autofill`)}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-amber-100 bg-amber-600/90 hover:bg-amber-500 border border-amber-300/60 px-3 py-1.5 rounded-sm shadow-lg transition-colors"
                      data-testid={`gap-fill-${c.slug}`}
                      title="This channel is on the demo fallback — schedule content to fill the gap"
                    >
                      <Wand2 className="w-3.5 h-3.5" strokeWidth={2} /> Fill this gap
                    </button>
                  )}

                  <div className="absolute top-2 bottom-2 right-2 z-10 flex items-center">
                    <AudioMeter
                      lufs={live.audio_lufs ?? -23}
                      active={soloChannelId === c.id}
                      videoEl={videoRefs.current[c.id]}
                      ducking={live.ducking}
                      truePeakDbtp={live.true_peak_dbtp}
                      loudnessTarget={audioProfileOf(c).loudness_target_lkfs}
                      loudnessSpec={audioProfileOf(c).loudness_spec}
                      channelLayout={audioProfileOf(c).primary_layout}
                      showLayout
                      testId={`meter-${c.slug}`}
                    />
                  </div>
                  {pubStateOf(c) !== "live" && (
                    <button
                      type="button"
                      onClick={() => togglePublishState(c)}
                      disabled={!canWrite || pubBusyId === c.id}
                      data-testid={`idle-overlay-${c.slug}`}
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/85 backdrop-grayscale text-center cursor-pointer disabled:cursor-default group/idle"
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-500" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 group-hover/idle:text-white transition-colors">
                        {pubBusyId === c.id ? "Publishing…" : "Idle — click to publish"}
                      </span>
                      <span className="font-mono text-[9px] text-zinc-500">worker stopped · schedule &amp; assets intact</span>
                    </button>
                  )}
                </div>
                <div className="p-3 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{c.slug}</div>
                      <div className="font-display font-bold">{c.name}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 justify-end max-w-[60%]">
                      <PublishTodayPanel channel={c} canWrite={canWrite} />
                      {c.outputs?.filter((o) => o.enabled).slice(0, 4).map((o) => (
                        <span key={o.protocol} className="text-[9px] font-mono uppercase border border-zinc-700 text-zinc-400 px-1 rounded-sm">
                          {o.protocol}
                        </span>
                      ))}
                    </div>
                  </div>
                  {cdnPlayback[c.id]?.currentStart && cdnPrefer[c.id] !== false && (
                    <div
                      className="flex items-stretch gap-1 mb-2 overflow-hidden"
                      data-testid={`epg-strip-${c.slug}`}
                    >
                      <div
                        className={`flex flex-col shrink-0 bg-emerald-950/60 border border-emerald-500/40 rounded-sm px-2 py-1 min-w-0 max-w-[46%] relative overflow-hidden ${canWrite ? "cursor-pointer hover:border-emerald-300" : ""}`}
                        onClick={() => canWrite && cdnPlayback[c.id]?.scheduleItemId && navigate(`/console/scheduler?channel=${c.id}&focus=${cdnPlayback[c.id].scheduleItemId}`)}
                        title={canWrite ? "Edit this slot in the Scheduler" : undefined}
                        data-testid={`epg-now-${c.slug}`}
                      >
                        <span className="text-[8px] font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Now · {cdnPlayback[c.id].currentStart}–{cdnPlayback[c.id].currentEnd}
                        </span>
                        <span className="text-[11px] font-medium text-emerald-50 truncate">{cdnPlayback[c.id].title}</span>
                        {(() => {
                          const pct = slotProgress(cdnPlayback[c.id]?.nextSwitchAt, cdnPlayback[c.id]?.currentSlotSec);
                          return pct == null ? null : (
                            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-emerald-950">
                              <div
                                className="h-full bg-emerald-400 transition-[width] duration-1000 ease-linear"
                                style={{ width: `${pct}%` }}
                                data-testid={`epg-progress-${c.slug}`}
                              />
                            </div>
                          );
                        })()}
                      </div>
                      {(cdnPlayback[c.id].upcoming || []).slice(0, 3).map((u, i) => (
                        <div
                          key={u.schedule_item_id || i}
                          className={`flex flex-col shrink min-w-0 flex-1 bg-zinc-900/70 border border-zinc-800 rounded-sm px-2 py-1 ${canWrite && u.schedule_item_id ? "cursor-pointer hover:border-fuchsia-400/60" : ""}`}
                          onClick={() => canWrite && u.schedule_item_id && navigate(`/console/scheduler?channel=${c.id}&focus=${u.schedule_item_id}`)}
                          title={`${u.start_time}–${u.end_time} · ${u.title || ""}${canWrite ? " · click to edit" : ""}`}
                          data-testid={`epg-next-${c.slug}-${i}`}
                        >
                          <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-500">{u.start_time}</span>
                          <span className="text-[11px] text-zinc-300 truncate">{u.title || "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <FlashWindowStrip
                    cdn={cdnPlayback[c.id]}
                    active={!!(cdnPlayback[c.id]?.hls_url && cdnPrefer[c.id] !== false && !adSwap[c.id])}
                    videoEl={videoRefs.current[c.id]}
                  />

                  {(() => {
                    const ap = audioProfileOf(c);
                    return (
                      <>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          <StatChip label="Bitrate" value={`${live.bitrate_mbps?.toFixed?.(2) || "0.00"} Mb/s`} tone="ok" />
                          <StatChip label={ap.loudness_spec === "EBU R.128" ? "LUFS" : "LKFS"} value={`${live.audio_lufs?.toFixed?.(1) || ap.loudness_target_lkfs.toFixed(1)}`} tone="ok" />
                          <StatChip label="FPS" value={`${c.fps}`} />
                          <StatChip label="SCTE-35" value={c.scte35_enabled ? "on" : "off"} tone={c.scte35_enabled ? "ok" : "warn"} />
                        </div>
                        <div
                          className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
                          data-testid={`audio-chips-${c.slug}`}
                        >
                          <StatChip label="Mix" value={ap.primary_layout} tone="ok" />
                          <StatChip label="Enc" value={`${ap.delivery_codec} ${ap.delivery_bitrate_kbps}k`} tone="ok" />
                          <StatChip label="Target" value={`${ap.loudness_target_lkfs} LKFS`} tone="warn" />
                          <StatChip label="Plant" value={`${ap.sample_rate_hz / 1000}k/${ap.bit_depth}b`} />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* System quick strip — hide during fullscreen for a cleaner pitch view */}
      {!fullscreenChannelId && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "CPU", value: "42%", icon: Cpu },
            { label: "RAM", value: "58%", icon: HardDrive },
            { label: "NET OUT", value: "420 Mb/s", icon: Wifi },
            { label: "AUDIO", value: "-23.1 LUFS", icon: Volume2 },
            { label: "ENGINES", value: `${channels.length} of ${channels.length}`, icon: Radio },
          ].map((s) => (
            <div key={s.label} className="panel px-3 py-2.5 flex items-center gap-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                <div className="font-display font-bold">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fullscreen "pitch mode" — the ONLY ShakaVideoTile for this channel is here */}
      {fsChannel && (() => {
        const c = fsChannel;
        const live = byId[c.id] || {};
        const status = live.status || c.status;
        const on = status === "on_air";
        const inAd = !!adSwap[c.id];
        return (
          <div
            data-testid="fullscreen-overlay"
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur flex flex-col p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm" style={{ background: c.color }} />
                <div className="font-display font-black text-3xl tracking-tight">{c.name}</div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-700 rounded-sm px-1.5 py-0.5">
                  {c.slug}
                </span>
                {on ? (
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-red-400">
                    <span className="dot-live" /> Live
                  </span>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{status}</span>
                )}
                {inAd && (
                  <span
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-950/80 border border-amber-500/60 px-2 py-0.5 rounded-sm animate-pulse"
                    data-testid="ad-bug-fs"
                  >
                    Ad Substitution · SCTE-35
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Master: <span className="text-emerald-400">{tick?.master_clock || "—"}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSettingsChannel(c)}
                  data-testid="fullscreen-channel-settings"
                  aria-label="Channel settings"
                  className="p-2 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white"
                >
                  <Settings2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setFullscreenChannelId(null)}
                  data-testid="fullscreen-close"
                  aria-label="Exit fullscreen (ESC)"
                  className="p-2 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-[1fr_320px] gap-4 min-h-0">
              <div className="flex flex-col gap-3 min-h-0">
                <div className="relative flex-1 bg-black border border-zinc-800 rounded-sm overflow-hidden min-h-0">
                  <ShakaVideoTile
                    ref={setVideoRef(c.id)}
                    url={streamUrlFor(c)}
                    trimIn={trimForCurrent(c).trimIn}
                    trimOut={trimForCurrent(c).trimOut}
                    poster={!inAd && cdnPrefer[c.id] !== false ? cdnPlayback[c.id]?.poster_url : null}
                    brandName={c.name}
                    brandColor={c.color}
                    muted={soloChannelId !== c.id}
                    volume={getVolume(c.id)}
                    testId={`shaka-fs-${c.slug}`}
                  />
                  <ChannelLogoBug channel={c} tenant={tenant} />
                  <BroadcastOverlays
                    cdn={cdnPlayback[c.id]}
                    active={!!(cdnPlayback[c.id]?.hls_url && cdnPrefer[c.id] !== false && !adSwap[c.id])}
                    channelColor={c.color}
                    videoEl={videoRefs.current[c.id]}
                  />
                  <ConfiguredGraphicsLayers
                    channelId={c.id}
                    active={!!(cdnPlayback[c.id]?.hls_url && cdnPrefer[c.id] !== false && !adSwap[c.id])}
                  />
                  <div className="absolute bottom-3 left-3 font-mono text-sm text-emerald-300 bg-black/70 border border-zinc-700 px-2 py-0.5">
                    {live.current_time || "--:--:--"}
                  </div>
                  <div className="absolute top-3 right-3 font-mono text-xs uppercase text-zinc-300 bg-black/70 border border-zinc-700 px-2 py-0.5">
                    {c.resolution} · {c.fps} fps
                  </div>
                  <div className="absolute bottom-3 right-3 font-mono text-xs text-zinc-300 bg-black/70 border border-zinc-700 px-2 py-0.5">
                    {live.viewers?.toLocaleString?.() || 0} viewers
                  </div>
                </div>
                {/* DVR scrub — reads seekable range from the video element */}
                <div className="panel shrink-0">
                  <div className="panel-header">
                    <span className="panel-title">DVR · Timeshift</span>
                    <span className="font-mono text-[10px] text-zinc-500">Shaka seek range</span>
                  </div>
                  <DvrScrubBar videoEl={videoRefs.current[c.id]} testId={`dvr-${c.slug}`} />
                </div>
              </div>

              <div className="flex flex-col gap-3 overflow-y-auto scrollbar-thin">
                <div className="panel p-3" data-testid="fs-audio-panel">
                  <div className="panel-title mb-2">Audio · Broadcast profile</div>
                  <div className="flex items-center gap-3">
                    <AudioMeter
                      lufs={live.audio_lufs ?? -23}
                      active={soloChannelId === c.id}
                      videoEl={videoRefs.current[c.id]}
                      ducking={live.ducking}
                      truePeakDbtp={live.true_peak_dbtp}
                      loudnessTarget={audioProfileOf(c).loudness_target_lkfs}
                      loudnessSpec={audioProfileOf(c).loudness_spec}
                      channelLayout={audioProfileOf(c).primary_layout}
                      showLayout
                      testId={`meter-fs-${c.slug}`}
                    />
                    <div className="flex-1">
                      <AudioControl
                        inline
                        soloed={soloChannelId === c.id}
                        volume={getVolume(c.id)}
                        onToggleSolo={() => toggleSolo(c.id)}
                        onVolumeChange={(v) => setVolume(c.id, v)}
                        testId={`audio-fs-${c.slug}`}
                      />
                    </div>
                  </div>
                  {(() => {
                    const ap = audioProfileOf(c);
                    const isAC3 = ap.delivery_codec === "AC-3" || ap.delivery_codec === "E-AC-3";
                    return (
                      <div className="mt-3 space-y-2 font-mono text-[11px]">
                        <div className="flex items-center gap-1 flex-wrap">
                          {(ap.channel_map || []).map((ch) => (
                            <span
                              key={ch}
                              data-testid={`ap-ch-${ch}`}
                              className={`px-1.5 py-0.5 rounded-sm border text-[10px] ${
                                ch === "LFE"
                                  ? "border-amber-500/60 text-amber-300 bg-amber-950/40"
                                  : "border-zinc-700 text-zinc-200 bg-black/40"
                              }`}
                              title={
                                {
                                  L: "Left front", R: "Right front",
                                  C: "Center", LFE: "Low-frequency effects (subwoofer)",
                                  Ls: "Left surround", Rs: "Right surround",
                                  Lb: "Left back", Rb: "Right back",
                                }[ch] || ch
                              }
                            >
                              {ch}
                            </span>
                          ))}
                          <span className="ml-auto text-zinc-500 uppercase tracking-widest text-[9px]">
                            {ap.primary_layout} primary
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <div className="border border-zinc-800 rounded-sm p-2 bg-black/30">
                            <div className="text-[9px] text-zinc-500 uppercase tracking-widest">Plant</div>
                            <div className="text-zinc-100">{ap.sample_rate_hz / 1000} kHz · {ap.bit_depth}-bit PCM</div>
                          </div>
                          <div className="border border-zinc-800 rounded-sm p-2 bg-black/30">
                            <div className="text-[9px] text-zinc-500 uppercase tracking-widest">Delivery</div>
                            <div className="text-zinc-100">{ap.delivery_codec} · {ap.delivery_bitrate_kbps} kbps</div>
                          </div>
                          <div className="border border-zinc-800 rounded-sm p-2 bg-black/30">
                            <div className="text-[9px] text-zinc-500 uppercase tracking-widest">Downmix</div>
                            <div className="text-zinc-100">
                              {ap.downmix_mode} {ap.downmix_embed_metadata ? "· embedded" : "· separate"}
                            </div>
                          </div>
                          <div className="border border-zinc-800 rounded-sm p-2 bg-black/30">
                            <div className="text-[9px] text-zinc-500 uppercase tracking-widest">Loudness</div>
                            <div className="text-zinc-100">
                              {ap.loudness_target_lkfs} LKFS {isAC3 && <span className="text-zinc-500">· dialnorm {ap.dialnorm}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          Compliance: <span className="text-amber-300">{ap.loudness_spec}</span>
                          {" · TP ceiling "}
                          {ap.true_peak_ceiling_dbtp} dBTP
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="panel p-3">
                  <div className="panel-title mb-2">Output signals</div>
                  <div className="space-y-1">
                    {(c.outputs || []).map((o) => (
                      <div key={o.protocol} className="flex items-center justify-between text-[11px] font-mono">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${o.enabled ? "bg-emerald-500" : "bg-zinc-600"}`} />
                          <span className={o.enabled ? "text-zinc-100" : "text-zinc-500"}>{o.protocol}</span>
                        </div>
                        <span className="text-zinc-500 truncate max-w-[200px]" title={o.url}>
                          {o.url.replace(/^[a-z]+:\/\//, "")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="panel p-3">
                  <div className="panel-title mb-2">Signal quality</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="border border-zinc-800 rounded-sm p-2">
                      <div className="text-[9px] font-mono uppercase text-zinc-500">Bitrate</div>
                      <div className="font-display font-bold text-emerald-400">
                        {live.bitrate_mbps?.toFixed?.(2) || "0.00"}
                        <span className="text-xs text-zinc-500 ml-1">Mb/s</span>
                      </div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm p-2">
                      <div className="text-[9px] font-mono uppercase text-zinc-500">FPS</div>
                      <div className="font-display font-bold">{c.fps}</div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm p-2">
                      <div className="text-[9px] font-mono uppercase text-zinc-500">SCTE-35</div>
                      <div className={`font-display font-bold ${c.scte35_enabled ? "text-emerald-400" : "text-zinc-500"}`}>
                        {c.scte35_enabled ? "ON" : "OFF"}
                      </div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm p-2">
                      <div className="text-[9px] font-mono uppercase text-zinc-500">EPG</div>
                      <div className={`font-display font-bold ${c.epg_enabled ? "text-emerald-400" : "text-zinc-500"}`}>
                        {c.epg_enabled ? "ON" : "OFF"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest text-center mt-auto">
                  Press ESC to exit
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ChannelSettingsDialog
        open={!!settingsChannel}
        channel={settingsChannel}
        onClose={() => setSettingsChannel(null)}
        onSaved={updateChannel}
      />
      <NewChannelDialog
        open={showNewChannel}
        onClose={() => setShowNewChannel(false)}
        onCreated={(ch) => setChannels((prev) => [...prev, ch])}
      />
    </div>
  );
}
